/**
 * Expression Resolver Utility
 *
 * Parses workflow expressions and resolves their values from run data.
 * Supports both Logic Apps style (@{...}) and JavaScript template literal style (${...}).
 *
 * Examples:
 * - @{body('Get_a_team')?.['id']} -> action: 'Get_a_team', path: ['body', 'id']
 * - ${triggerOutputs()['headers']} -> action: 'trigger', path: ['headers']
 * - ${body('Action_Name').property.nested} -> action: 'Action_Name', path: ['body', 'property', 'nested']
 */

export interface ParsedExpression {
  /** The original expression text */
  expression: string;
  /** The action or trigger name */
  actionName: string;
  /** Whether this references the trigger (vs an action) */
  isTrigger: boolean;
  /** Whether this is a variable reference */
  isVariable?: boolean;
  /** The variable name (if isVariable is true) */
  variableName?: string;
  /** The path to extract from the output (e.g., ['body', 'id']) */
  path: string[];
  /** The root accessor (body, outputs, headers, etc.) */
  rootAccessor: 'body' | 'outputs' | 'headers' | 'statusCode' | 'queries' | 'variables' | 'unknown';
}

export interface ResolvedExpression {
  /** The parsed expression info */
  parsed: ParsedExpression;
  /** The resolved value, or undefined if not found */
  value: unknown;
  /** Whether the value was successfully resolved */
  resolved: boolean;
  /** Error message if resolution failed */
  error?: string;
}

/**
 * Parse a single expression to extract action name and property path.
 */
export function parseExpression(expression: string): ParsedExpression | null {
  // Remove ${...} or @{...} wrapper, or handle inline @function() format
  let inner = expression.trim();
  if (inner.startsWith('${') && inner.endsWith('}')) {
    inner = inner.slice(2, -1).trim();
  } else if (inner.startsWith('@{') && inner.endsWith('}')) {
    inner = inner.slice(2, -1).trim();
  } else if (inner.startsWith('@') && !inner.startsWith('@{')) {
    // Inline @function() format like @variables('name'), @body('action')
    inner = inner.slice(1).trim();
  } else {
    // Not a recognized expression format
    return null;
  }

  // Try to parse variable expressions first
  const variableResult = parseVariableExpression(inner);
  if (variableResult) {
    return {
      expression,
      ...variableResult,
    };
  }

  // Try to parse trigger expressions
  const triggerResult = parseTriggerExpression(inner);
  if (triggerResult) {
    return {
      expression,
      ...triggerResult,
    };
  }

  // Try to parse action expressions
  const actionResult = parseActionExpression(inner);
  if (actionResult) {
    return {
      expression,
      ...actionResult,
    };
  }

  return null;
}

/**
 * Parse variable expressions like:
 * - variables('pokemonName')
 * - variables('name')['property']
 */
function parseVariableExpression(expr: string): Omit<ParsedExpression, 'expression'> | null {
  const variableMatch = expr.match(/^variables\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  if (variableMatch && variableMatch[1]) {
    const variableName = variableMatch[1];
    const remaining = expr.slice(variableMatch[0].length);
    const path = extractPath(remaining);
    return {
      actionName: '__variables__',
      isTrigger: false,
      isVariable: true,
      variableName,
      path,
      rootAccessor: 'variables',
    };
  }
  return null;
}

/**
 * Parse trigger expressions like:
 * - triggerBody()
 * - triggerOutputs()
 * - triggerOutputs()['headers']
 * - trigger().outputs.body
 */
function parseTriggerExpression(expr: string): Omit<ParsedExpression, 'expression'> | null {
  // triggerBody() - shorthand for trigger body
  if (expr.startsWith('triggerBody()')) {
    const path = extractPath(expr.slice('triggerBody()'.length));
    return {
      actionName: 'trigger',
      isTrigger: true,
      path: ['body', ...path],
      rootAccessor: 'body',
    };
  }

  // triggerOutputs() - full outputs including headers, statusCode, etc.
  if (expr.startsWith('triggerOutputs()')) {
    const path = extractPath(expr.slice('triggerOutputs()'.length));
    // Determine root accessor from first path element
    const firstPathElement = path[0];
    const rootAccessor = firstPathElement ? mapToRootAccessor(firstPathElement) : 'outputs';
    return {
      actionName: 'trigger',
      isTrigger: true,
      path,
      rootAccessor,
    };
  }

  // trigger() - generic trigger reference
  if (expr.startsWith('trigger()')) {
    const path = extractPath(expr.slice('trigger()'.length));
    const firstPathElement = path[0];
    const rootAccessor = firstPathElement ? mapToRootAccessor(firstPathElement) : 'outputs';
    return {
      actionName: 'trigger',
      isTrigger: true,
      path,
      rootAccessor,
    };
  }

  return null;
}

/**
 * Parse action expressions like:
 * - body('Action_Name')
 * - body('Action_Name')?.['id']
 * - body('Action_Name').property.nested
 * - outputs('Action_Name')
 * - outputs('Action_Name')['headers']
 */
function parseActionExpression(expr: string): Omit<ParsedExpression, 'expression'> | null {
  // body('Action_Name') pattern
  const bodyMatch = expr.match(/^body\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  if (bodyMatch && bodyMatch[1]) {
    const actionName = bodyMatch[1];
    const remaining = expr.slice(bodyMatch[0].length);
    const path = extractPath(remaining);
    return {
      actionName,
      isTrigger: false,
      path: ['body', ...path],
      rootAccessor: 'body',
    };
  }

  // outputs('Action_Name') pattern
  const outputsMatch = expr.match(/^outputs\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  if (outputsMatch && outputsMatch[1]) {
    const actionName = outputsMatch[1];
    const remaining = expr.slice(outputsMatch[0].length);
    const path = extractPath(remaining);
    const firstPathElement = path[0];
    const rootAccessor = firstPathElement ? mapToRootAccessor(firstPathElement) : 'outputs';
    return {
      actionName,
      isTrigger: false,
      path,
      rootAccessor,
    };
  }

  // actions('Action_Name').outputs pattern
  const actionsMatch = expr.match(/^actions\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  if (actionsMatch && actionsMatch[1]) {
    const actionName = actionsMatch[1];
    const remaining = expr.slice(actionsMatch[0].length);
    const path = extractPath(remaining);
    const firstPathElement = path[0];
    const rootAccessor = firstPathElement ? mapToRootAccessor(firstPathElement) : 'outputs';
    return {
      actionName,
      isTrigger: false,
      path,
      rootAccessor,
    };
  }

  return null;
}

/**
 * Extract property path from remaining expression.
 * Handles both bracket notation ['prop'] and dot notation .prop
 * Also handles optional chaining ?.['prop'] and ?.prop
 */
function extractPath(remaining: string): string[] {
  const path: string[] = [];
  let current = remaining.trim();

  while (current.length > 0) {
    // Optional chaining marker. Handle all three forms emitted by the system:
    //   ?.['x'] → strip "?." so the bracket regex matches "['x']"
    //   ?.x     → strip "?"  so the dot regex matches ".x"
    //   ?['x']  → strip "?"  so the bracket regex matches "['x']" (legacy)
    if (current.startsWith('?.[')) {
      current = current.slice(2);
      continue;
    }
    if (current.startsWith('?')) {
      current = current.slice(1);
      continue;
    }

    // Bracket notation: ['prop'] or ["prop"]
    const bracketMatch = current.match(/^\s*\[\s*['"]([^'"]+)['"]\s*\]/);
    if (bracketMatch && bracketMatch[1]) {
      path.push(bracketMatch[1]);
      current = current.slice(bracketMatch[0].length);
      continue;
    }

    // Bracket notation with number: [0]
    const indexMatch = current.match(/^\s*\[\s*(\d+)\s*\]/);
    if (indexMatch && indexMatch[1]) {
      path.push(indexMatch[1]);
      current = current.slice(indexMatch[0].length);
      continue;
    }

    // Dot notation: .prop
    const dotMatch = current.match(/^\s*\.([a-zA-Z_$][a-zA-Z0-9_$]*)/);
    if (dotMatch && dotMatch[1]) {
      path.push(dotMatch[1]);
      current = current.slice(dotMatch[0].length);
      continue;
    }

    // No more valid path segments
    break;
  }

  return path;
}

/**
 * Map a path element to root accessor type
 */
function mapToRootAccessor(pathElement: string): ParsedExpression['rootAccessor'] {
  switch (pathElement.toLowerCase()) {
    case 'body':
      return 'body';
    case 'headers':
      return 'headers';
    case 'statuscode':
      return 'statusCode';
    case 'queries':
      return 'queries';
    case 'outputs':
      return 'outputs';
    default:
      return 'unknown';
  }
}

/**
 * Resolve an expression value from run output data.
 */
export function resolveExpressionValue(
  parsed: ParsedExpression,
  outputData: unknown
): ResolvedExpression {
  if (!outputData || typeof outputData !== 'object') {
    return {
      parsed,
      value: undefined,
      resolved: false,
      error: 'No output data available',
    };
  }

  try {
    let value: unknown = outputData;

    // Navigate the path
    for (const key of parsed.path) {
      if (value === null || value === undefined) {
        return {
          parsed,
          value: undefined,
          resolved: false,
          error: `Path "${parsed.path.join('.')}" not found in output`,
        };
      }

      if (typeof value !== 'object') {
        return {
          parsed,
          value: undefined,
          resolved: false,
          error: `Cannot access "${key}" on non-object value`,
        };
      }

      value = (value as Record<string, unknown>)[key];
    }

    return {
      parsed,
      value,
      resolved: true,
    };
  } catch (error) {
    return {
      parsed,
      value: undefined,
      resolved: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Format a resolved value for display.
 * Handles different types appropriately.
 */
export function formatResolvedValue(value: unknown, maxLength: number = 400): string {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'string') {
    if (value.length > maxLength) {
      return `${value.slice(0, maxLength)}...`;
    }
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const preview = JSON.stringify(value);
    if (preview.length > maxLength) {
      return `[Array with ${value.length} items]`;
    }
    return preview;
  }
  if (typeof value === 'object') {
    const preview = JSON.stringify(value);
    if (preview.length > maxLength) {
      const keys = Object.keys(value);
      const maxKeysToShow = 5;
      if (keys.length <= maxKeysToShow) {
        return `{${keys.join(', ')}}`;
      }
      const shownKeys = keys.slice(0, maxKeysToShow).join(', ');
      return `{${shownKeys}, ... +${keys.length - maxKeysToShow} more}`;
    }
    return preview;
  }
  return String(value);
}

/**
 * Parse all expressions in a text and return parsed info for each.
 */
export function parseAllExpressions(text: string): ParsedExpression[] {
  const results: ParsedExpression[] = [];

  // Match ${...} expressions
  const jsRegex = /\$\{[^}]+\}/g;
  let match;
  while ((match = jsRegex.exec(text)) !== null) {
    const parsed = parseExpression(match[0]);
    if (parsed) {
      results.push(parsed);
    }
  }

  // Match @{...} expressions
  const laRegex = /@\{[^}]+\}/g;
  while ((match = laRegex.exec(text)) !== null) {
    const parsed = parseExpression(match[0]);
    if (parsed) {
      results.push(parsed);
    }
  }

  return results;
}

/**
 * Get unique action names referenced in expressions.
 */
export function getReferencedActions(expressions: ParsedExpression[]): string[] {
  const actions = new Set<string>();
  for (const expr of expressions) {
    if (!expr.isTrigger) {
      actions.add(expr.actionName);
    }
  }
  return Array.from(actions);
}

/**
 * Check if any expression references the trigger.
 */
export function referencesTrigger(expressions: ParsedExpression[]): boolean {
  return expressions.some(expr => expr.isTrigger);
}

// Re-export the non-evaluating expression helpers. The sync `evaluateExpression` /
// `evaluateTemplate` are deliberately not surfaced here — runtime code must use the async
// worker-based evaluator; tests import the sync evaluator from '@/utils/expressions/evaluator'.
export {
  canEvaluate,
} from './evaluator';
export type { EvaluationContext, EvaluationResult } from './types';
